// src/components/pages/DevicesPage/DevicesPage.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../services/useUserData';
import { useAppStore } from '../../store/useAppStore';
import { Device } from '../../services/api_url';
import Loader from '../../widgets/Loader/Loader';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import {
    PendingDeviceIcon,
    AddDeviceIcon,
    SettingsIcon,
    StatsIcon,
    ProtocolIcon
} from '../../assets/images/DeviceIcons';
import './DevicesPage.scss';

function DevicesPage() {
    const navigate = useNavigate();
    const { telegramId, loading: userLoading } = useUserData();
    const {
        devices: cachedDevices,
        loadDevices,
        isLoading: storeLoading,
        error: storeError
    } = useAppStore();

    const [pressedDeviceId, setPressedDeviceId] = useState<number | null>(null);
    const [localError, setLocalError] = useState<string | null>(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const loadAttemptedRef = useRef(false);
    const lastUpdateRef = useRef<number>(0); // Для отслеживания времени последнего обновления

    // Функция для принудительного обновления данных
    const refreshDevicesData = useCallback(async () => {
        if (!telegramId) return;

        try {
            await loadDevices(telegramId);
            lastUpdateRef.current = Date.now();
            setLocalError(null);
        } catch (err) {
            setLocalError('Не удалось обновить устройства');
        }
    }, [telegramId, loadDevices]);

    // Мемоизированные функции
    const handleDevicePress = useCallback((deviceId: number) => {
        setPressedDeviceId(deviceId);

        setTimeout(() => {
            navigate(`/device/${deviceId}`);
        }, 160);

        setTimeout(() => {
            setPressedDeviceId(null);
        }, 420);
    }, [navigate]);

    const handleAddDevice = useCallback(() => {
        navigate('/payment');
    }, [navigate]);

    const getDeviceStatus = useCallback((device: Device): { color: 'green' | 'yellow'; text: string } => {
        const expiresAt = new Date(device.expires_at);
        const now = new Date();
        const isExpired = expiresAt < now;

        if (!device.is_configured) {
            return { color: 'yellow', text: 'Требуется настройка' };
        }

        if (isExpired) {
            return { color: 'yellow', text: 'Подписка истекла' };
        }

        return { color: 'green', text: 'Активно' };
    }, []);

    const getCornerIcon = useCallback((device: Device) => {
        const status = getDeviceStatus(device);
        if (status.color === 'green') {
            return <StatsIcon size={26} color="#57F2D3" />;
        }
        return <PendingDeviceIcon size={26} color="#F3C969" />;
    }, [getDeviceStatus]);

    const getDeviceName = useCallback((device: Device): string => {
        if (device.name) return device.name;
        if (!device.is_configured) return 'Ожидает настройки';
        if (device.protocol_name) return `${device.protocol_name} устройство`;
        return 'Устройство';
    }, []);

    const formatDate = useCallback((dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }, []);

    const getDeviceTimeInfo = useCallback((device: Device): string => {
        return `до ${formatDate(device.expires_at)}`;
    }, [formatDate]);

    const getProtocolDisplayName = useCallback((protocol?: string) => {
        if (!protocol) return '';
        if (protocol.toLowerCase() === 'awg') return 'AmneziaWG';
        if (protocol.toLowerCase() === 'wireguard') return 'WireGuard';
        return protocol;
    }, []);

    // Мемоизированные вычисления
    const devices = cachedDevices?.devices || [];
    const deviceLimit = cachedDevices?.device_limit || 0;

    const isLoading = userLoading || storeLoading || isInitialLoad;

    const sortedDevices = useMemo(() => {
        return [...devices].sort((a, b) => {
            const aExpired = new Date(a.expires_at) < new Date();
            const bExpired = new Date(b.expires_at) < new Date();

            if (aExpired && !bExpired) return -1;
            if (!aExpired && bExpired) return 1;

            return new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime();
        });
    }, [devices]);

    const { activeDevices, pendingDevices } = useMemo(() => {
        const active = sortedDevices.filter(d => getDeviceStatus(d).color === 'green');
        const pending = sortedDevices.filter(d => getDeviceStatus(d).color === 'yellow');
        return { activeDevices: active, pendingDevices: pending };
    }, [sortedDevices, getDeviceStatus]);

    const safeDeviceLimit = useMemo(() => Math.max(deviceLimit, 0), [deviceLimit]);
    const availableSlots = useMemo(() => Math.max(safeDeviceLimit - devices.length, 0), [safeDeviceLimit, devices.length]);
    const activePercentage = useMemo(() => safeDeviceLimit > 0 ? (activeDevices.length / safeDeviceLimit) * 100 : 0, [activeDevices.length, safeDeviceLimit]);

    const error = storeError || localError;


    // Эффект для обновления данных при монтировании
    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();

            const version = tg.version;
            const versionNumber = parseFloat(version);

            if (versionNumber >= 6.2) {
                tg.setHeaderColor?.('#000000');
                tg.setBackgroundColor?.('#000000');
            }
        }
    }, []);

    // Загрузка данных с правильным управлением состоянием
    useEffect(() => {
        const loadDevicesData = async () => {
            if (!telegramId) {
                if (cachedDevices) {
                    setIsInitialLoad(false);
                }
                return;
            }

            // Если данные уже есть и они для правильного пользователя
            if (cachedDevices && cachedDevices.user_id === telegramId && !loadAttemptedRef.current) {
                setIsInitialLoad(false);
                lastUpdateRef.current = Date.now();
                return;
            }

            if (loadAttemptedRef.current) return;

            loadAttemptedRef.current = true;

            try {
                await loadDevices(telegramId);
                lastUpdateRef.current = Date.now();
            } catch (err) {
                setLocalError('Не удалось загрузить устройства');
            } finally {
                setTimeout(() => {
                    setIsInitialLoad(false);
                }, 100);
            }
        };

        loadDevicesData();
    }, [telegramId, cachedDevices, loadDevices]);

    // Эффект для обновления данных при возврате из DeviceDetailPage
    useEffect(() => {
        // Слушаем событие изменения маршрута
        const handleRouteChange = () => {
            if (telegramId && window.location.pathname === '/devices') {
                refreshDevicesData();
            }
        };

        // Используем PopStateEvent для отслеживания навигации назад/вперед
        window.addEventListener('popstate', handleRouteChange);

        // Также обновляем при монтировании компонента, если мы на странице устройств
        if (telegramId && window.location.pathname === '/devices') {
            refreshDevicesData();
        }

        return () => {
            window.removeEventListener('popstate', handleRouteChange);
        };
    }, [telegramId, refreshDevicesData]);

    // Рендер функций
    const renderDeviceCard = useCallback((device: Device, variant: 'green' | 'yellow') => {
        return (
            <article
                key={device.id}
                className={`device-card ${variant} clickable ${pressedDeviceId === device.id ? 'is-pressed' : ''}`}
                onClick={() => handleDevicePress(device.id)}
            >
                <div className="menu-item-like">
                    <div className={`device-side-icon ${variant}`}>
                        {getCornerIcon(device)}
                    </div>

                    <div className="device-main-copy">
                        <div className="device-name-row compact">
                            <h3 className="device-name">{getDeviceName(device)}</h3>
                            <span className={`status-dot ${variant === 'green' ? 'green' : 'yellow'}`} />
                        </div>

                        <div className="device-meta-row">
                            {device.protocol_name && (
                                <span className={`device-chip protocol ${variant}`}>
                                    <ProtocolIcon size={12} color={variant === 'green' ? '#57F2D3' : '#F3C969'} />
                                    {getProtocolDisplayName(device.protocol_name)}
                                </span>
                            )}

                            <span className="device-chip time">
                                {getDeviceTimeInfo(device)}
                            </span>

                            {device.server_name && (
                                <span className="device-chip server">
                                    {device.server_name}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="menu-right-like">
                        <button
                            className={`device-settings ${pressedDeviceId === device.id ? 'spin' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDevicePress(device.id);
                            }}
                        >
                            <SettingsIcon size={18} />
                        </button>
                    </div>
                </div>
            </article>
        );
    }, [pressedDeviceId, handleDevicePress, getCornerIcon, getDeviceName, getDeviceTimeInfo, getProtocolDisplayName]);

    if (isLoading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="devices-page loading">
                    <div className="loader">
                        <Loader />
                    </div>
                </div>
            </div>
        );
    }

    if (!telegramId && !cachedDevices) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="devices-page">
                    <div className="devices-empty-state">
                        <div className="devices-empty-icon">🔒</div>
                        <h2>Необходима авторизация</h2>
                        <p>Войдите в систему, чтобы просматривать устройства и управлять подключениями.</p>
                        <button className="devices-primary-button" onClick={() => navigate('/key')}>
                            Перейти к авторизации
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar />

            <div className="devices-page">
                <div className="devices-shell">
                    <section className="devices-hero-card compact-header">
                        <div className="devices-hero-topline">
                            <div>
                                <h1 className="devices-hero-title">Устройства</h1>
                                <p className="devices-hero-subtitle">Подключения, настройка и доступные слоты</p>
                            </div>
                        </div>

                        <div className={`devices-hero-stats ${availableSlots > 0 ? '' : 'two-cols'}`}>
                            <div className="hero-stat-card active">
                                <div className="hero-stat-top">
                                    <StatsIcon size={18} />
                                    <span>Активные</span>
                                </div>
                                <div className="hero-stat-value">{activeDevices.length}</div>
                                <div className="hero-stat-caption">Подключено</div>
                            </div>

                            <div className="hero-stat-card pending">
                                <div className="hero-stat-top">
                                    <PendingDeviceIcon size={18} color="#F3C969" />
                                    <span>Ожидают</span>
                                </div>
                                <div className="hero-stat-value">{pendingDevices.length}</div>
                                <div className="hero-stat-caption">Ожидают активации</div>
                            </div>

                            {availableSlots > 0 && (
                                <div className="hero-stat-card slots">
                                    <div className="hero-stat-top">
                                        <AddDeviceIcon size={18} color="#57F2D3" />
                                        <span>Свободно</span>
                                    </div>
                                    <div className="hero-stat-value">{availableSlots}</div>
                                    <div className="hero-stat-caption">доступно для новых устройств</div>
                                </div>
                            )}
                        </div>

                        <div className="devices-progress-card">
                            <div className="devices-progress-head">
                                <span>Заполненность слотов</span>
                                <span>{activeDevices.length} / {safeDeviceLimit || 0}</span>
                            </div>

                            <div className="devices-progress">
                                <div className="progress-bar">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${Math.min(activePercentage, 100)}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {error && (
                        <div className="devices-inline-error">
                            {error}
                        </div>
                    )}

                    {activeDevices.length > 0 && (
                        <section className="device-section profile-style">
                            <div className="section-head">
                                <div>
                                    <div className="section-title">Активные устройства</div>
                                    <div className="section-subtitle">Устройства с действующим доступом</div>
                                </div>
                                <div className="section-badge green">{activeDevices.length}</div>
                            </div>

                            <div className="devices-grid profile-style">
                                {activeDevices.map((device) => renderDeviceCard(device, 'green'))}
                            </div>
                        </section>
                    )}

                    {pendingDevices.length > 0 && (
                        <section className="device-section profile-style">
                            <div className="section-head">
                                <div>
                                    <div className="section-title">Ожидают настройки</div>
                                    <div className="section-subtitle">Требуют завершения настройки</div>
                                </div>
                                <div className="section-badge yellow">{pendingDevices.length}</div>
                            </div>

                            <div className="devices-grid profile-style">
                                {pendingDevices.map((device) => renderDeviceCard(device, 'yellow'))}
                            </div>
                        </section>
                    )}

                    {availableSlots > 0 && (
                        <section className="device-section profile-style">
                            <div className="section-head">
                                <div>
                                    <div className="section-title">Свободные слоты</div>
                                    <div className="section-subtitle">Можно добавить новые устройства</div>
                                </div>
                                <div className="section-badge cyan">{availableSlots}</div>
                            </div>

                            <div className="devices-grid profile-style">
                                {Array.from({ length: Math.min(availableSlots, 10) }, (_, index) => (
                                    <article
                                        key={`empty-${index}`}
                                        className="device-card empty"
                                        onClick={handleAddDevice}
                                    >
                                        <div className="menu-item-like">
                                            <div className="device-side-icon empty">
                                                <AddDeviceIcon size={26} color="#57F2D3" />
                                            </div>

                                            <div className="device-main-copy">
                                                <div className="device-name-row compact">
                                                    <h3 className="device-name">Свободное место</h3>
                                                </div>

                                                <div className="device-meta-row">
                                                    <span className="device-chip time">Добавить по тарифу</span>
                                                </div>

                                                <div className="device-server">
                                                    Нажмите, чтобы перейти к оформлению
                                                </div>
                                            </div>

                                            <div className="menu-right-like">
                                                <div className="device-settings static">
                                                    <AddDeviceIcon size={16} color="#57F2D3" />
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </section>
                    )}

                    {availableSlots > 0 && devices.length > 0 && (
                        <button className="add-device-cta" onClick={handleAddDevice}>
                            <AddDeviceIcon size={18} color="#57F2D3" />
                            <span>Приобрести дополнительные устройства</span>
                        </button>
                    )}

                    {devices.length === 0 && availableSlots === 0 && !error && !isLoading && (
                        <div className="devices-empty-state compact">
                            <div className="devices-empty-icon">◎</div>
                            <h2>Устройства пока не найдены</h2>
                            <p>Когда появятся подключенные устройства или свободные слоты, они отобразятся здесь.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DevicesPage;